import { Range } from "@daangn/idl/rocketchat/api/api";
/**
 * @fileoverview 채널 정보와 unreadCount를 서버와 동기화합니다.
 * 채널과 관련된 일부 데이터가 변경되었을때, 클라이언트에서 로직으로 처리가 가능하다면 이 함수를 사용하지 않아도 됩니다.
 * 클라이언트에서 복잡한 로직을 실행하는게 부담이 되거나, 반드시 서버의 상태와 동기화 되어야 할 때 이 함수를 사용합니다.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isEmpty, isNil } from "lodash";
import { getMessagesByChannelId } from "~/rocketchat/chatMessage/api/getMessagesByChannelId";
import { upsertChatMessageList } from "~/rocketchat/chatMessage/chatMessageSlice";
import { createThunkName } from "~/rocketchat/redux/createThunkNamespace";
import type { ThunkBase } from "~/rocketchat/redux/types";
import { SocketClient } from "~/rocketchat/socket/socketClient";
import { getChannel } from "../api/getChannel";
import { getUnreadCount } from "../api/getUnreadCount";
import {
  channelSelectors,
  updateChannelAggregationData,
  upsertChannel,
} from "../channelSlice";

type ReconcileChannelThunkPayload = {
  channelId: string;
  criteriaMessageId?: string;
};
export const createReconcileChannelThunk = (tag: string) => {
  return createAsyncThunk<void, ReconcileChannelThunkPayload, ThunkBase>(
    createThunkName(["reconcileChannelThunk", tag].filter(Boolean).join(":")),
    async (
      { channelId, criteriaMessageId }: ReconcileChannelThunkPayload,
      { dispatch, getState },
    ) => {
      try {
        const currentUnreadCount = getState().channel.unreadCount;
        const currentChannel = channelSelectors.selectById(
          getState().channel,
          channelId,
        );

        if (isNil(currentChannel)) {
          throw new Error(
            `reconcileChannelThunk: Channel not found: ${channelId}`,
          );
        }

        const socket = SocketClient.getInstance().getWebSocket();
        const [normalizedChannel, unreadCountResponse, messagesResponse] =
          await Promise.all([
            getChannel({ channelId }, socket),
            getUnreadCount(socket),
            getMessagesByChannelId(
              {
                channelId,
                criteriaMessageId:
                  criteriaMessageId ?? currentChannel.last_message_id,
                limit: 5,
                range: Range.LESS_EQUAL,
              },
              socket,
            ),
          ]);

        if (!isEmpty(messagesResponse.messages)) {
          dispatch(upsertChatMessageList(messagesResponse.messages));
        }
        dispatch(upsertChannel(normalizedChannel.channel ?? currentChannel));
        dispatch(
          updateChannelAggregationData({
            unreadCount:
              unreadCountResponse?.unread_count ?? currentUnreadCount,
          }),
        );
      } catch (error) {
        console.error(error);
      }
    },
  );
};
